import React, { useState, useRef, lazy, Suspense } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Zoom } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/zoom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ProductImage } from "@/components/product/ProductImage";

// Lazy load the lightbox to reduce initial bundle size
const PhotoSlider = lazy(() =>
  import("react-photo-view").then((module) => ({ default: module.PhotoSlider }))
);
import { PhotoProvider } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";

const ImageSlider = ({ images = [], productName }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const swiperRef = useRef(null);

  if (!images.length) {
    return (
      <div className="bg-muted rounded-lg flex items-center justify-center h-64 sm:h-72 md:h-80">
        <span className="text-muted-foreground">لا توجد صور</span>
      </div>
    );
  }

  // Map images to the format react-photo-view expects
  const lightboxImages = images.map((img) => ({
    src: img.big || img.url || img.medium || img.small || "",
    alt: productName,
  }));

  const handleThumbClick = (index) => {
    setSelectedIndex(index);
    swiperRef.current?.slideTo(index);
  };

  const handleMainImageClick = () => {
    setLightboxVisible(true);
  };

  return (
    <div dir="rtl" className="flex gap-4">
      {/* Thumbnails strip – unchanged */}
      {images.length > 1 && (
        <div className="w-20 flex-shrink-0">
          <ScrollArea className="h-64 sm:h-72 md:h-80">
            <div className="space-y-2">
              {images.map((img, idx) => (
                <div
                  key={img.id}
                  className={`cursor-pointer overflow-hidden rounded-md border transition-all ${
                    idx === selectedIndex
                      ? "border-brand ring-1 ring-brand"
                      : "border-border hover:border-brand"
                  }`}
                  onClick={() => handleThumbClick(idx)}
                >
                  <div className="aspect-square w-full">
                    <ProductImage
                      image={img}
                      size="thumbnail"
                      className="w-full h-full object-cover"
                      alt={`${productName} - صورة`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Main Swiper – now clickable to open lightbox */}
      <div className="flex-1 relative overflow-hidden rounded-lg border border-border bg-background">
        <div onClick={handleMainImageClick} className="cursor-pointer">
          <Swiper
            loop={true}
            zoom={true}
            modules={[Navigation, Zoom]}
            className="h-64 sm:h-72 md:h-80 w-full"
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => setSelectedIndex(swiper.realIndex)}
            initialSlide={selectedIndex}
          >
            {images.map((img) => (
              <SwiperSlide key={img.id}>
                <div className="swiper-zoom-container w-full h-full">
                  <ProductImage
                    image={img}
                    size="medium"
                    className="w-full h-full object-contain"
                    alt={productName}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Navigation Buttons – same as before */}
        <Button
          size="icon"
          variant="secondary"
          className="absolute top-1/2 -translate-y-1/2 left-2 z-20 rounded-full shadow-md h-8 w-8"
          onClick={() => swiperRef.current?.slidePrev()}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className="absolute top-1/2 -translate-y-1/2 right-2 z-20 rounded-full shadow-md h-8 w-8"
          onClick={() => swiperRef.current?.slideNext()}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Lazy-loaded modern lightbox */}
      {lightboxVisible && (
        <Suspense fallback={null}>
          <PhotoProvider maskOpacity={0.88} maskClosable={true}>
            <PhotoSlider
              images={lightboxImages}
              visible={lightboxVisible}
              onClose={() => setLightboxVisible(false)}
              index={selectedIndex}
              onIndexChange={setSelectedIndex}
            />
          </PhotoProvider>
        </Suspense>
      )}
    </div>
  );
};

export default ImageSlider;